<script setup lang="ts">
import { useDemoAccueil } from "~/composables/useDemoAccueil";
/**
 * État d'erreur "ressource non disponible" partagé /a/[slug] et /b/[slug].
 * Différence : icône + titre + message selon le type de page.
 *
 * `icon` accepte DEUX formes :
 *  - un nom d'icône Iconify/Lucide (ex `i-lucide-clock`) → rendu <UIcon>
 *  - un chemin de SVG maison (ex `/svg/hands-no.svg`) → rendu <img> (illustration
 *    « mains » de la marque, cf. pages de succès). Karim 2026-07-08.
 */
const props = defineProps<{
  icon: string;
  title: string;
  message: string;
  /** Rayon de l'en-tête, comme sur la page (billetterie / adhésion). */
  rayon?: "evenements" | "adhesions" | "cagnottes";
}>();

// Démo de la home : la page s'y affiche sans en-tête ni pied de page (la
// fenêtre de navigateur simulée en tient lieu), même en erreur.
const { estIntegree } = useDemoAccueil();

// Un chemin `/svg/...` = illustration maison (img) ; sinon icône Iconify (UIcon).
const isHouseSvg = computed(() => props.icon.startsWith("/svg/"));

// Slot « suggestions » (Karim 2026-08-01) : contenus actifs proposés sous la
// carte d'erreur (cagnottes/événements/adhésions en cours) — la page 404 n'est
// plus un cul-de-sac. Quand le slot est fourni, le layout passe de « plein
// écran exact » à « scrollable + footer ». Sans slot : comportement historique
// inchangé (h-screen, pas de footer — Karim 2026-07-02).
const slots = useSlots();
const hasSuggestions = computed(() => !!slots.suggestions);

const { t } = useI18n();
</script>

<template>
  <div
    class="flex flex-col bg-[#f7f9fd] dark:bg-[#0a0a0a]"
    :class="hasSuggestions ? 'min-h-screen' : 'h-screen'"
  >
    <AppHeader v-if="!estIntegree" :rayon="props.rayon" />

    <div
      class="flex items-center justify-center px-4 pt-24 pb-8"
      :class="hasSuggestions ? 'pt-28 pb-12' : 'flex-1'"
    >
      <div class="w-full max-w-lg bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] border border-gray-100 dark:border-gray-800 px-6 sm:px-10 py-10 text-center">
        <!-- Illustration maison (« mains ») rendue directement ; sinon icône
             Iconify dans le carré gris (états draft/not-found). -->
        <img
          v-if="isHouseSvg"
          :src="icon"
          alt=""
          class="w-24 h-24 mx-auto mb-6 object-contain"
        />
        <div v-else class="w-20 h-20 mx-auto rounded-3xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-6">
          <UIcon :name="icon" class="size-10 text-gray-300 dark:text-gray-600" />
        </div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">{{ title }}</h1>
        <p class="text-gray-500 dark:text-gray-400 leading-relaxed mb-8">
          {{ message }}
        </p>
        <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
          <NuxtLink to="/recherche/associations" class="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-sm font-semibold whitespace-nowrap bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-90 transition-opacity">
            <UIcon name="i-lucide-heart" class="size-4 shrink-0" />
            {{ t("association.publicPage.errorState.decouvrirAssociations") }}
          </NuxtLink>
          <NuxtLink to="/" class="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-sm font-medium whitespace-nowrap text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <UIcon name="i-lucide-home" class="size-4 shrink-0" />
            {{ t("association.publicPage.errorState.retourAccueil") }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- Suggestions de contenus actifs (optionnel) + footer -->
    <template v-if="hasSuggestions">
      <slot name="suggestions" />
      <AppFooter v-if="!estIntegree" class="mt-auto" />
    </template>
  </div>
</template>
